import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { AuthContext } from 'contexts/AuthContext';
import { GridContext } from 'contexts/GridContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { useModal } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Input from 'components/Input';
import moment from 'moment';
import styles from './styles';

const Modal = ({ item, documento_id, onSubmit }) => {
  const defaultValues = { historico: item?.historico || '' };
  const { control, handleSubmit } = useForm({ defaultValues });
  const { closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { postLoading } = useContext(DocumentosContext);

  const onConfirm = (values) => {
    const data = {
      Registros: [
        {
          id: item?.id,
          documento_id,
          users_id: user?.id,
          historico: values?.historico,
          datahora: item?.datahora ?? moment().format('YYYY-MM-DD HH:mm:ss'),
        },
      ],
    };

    onSubmit(data);
  };

  return (
    <>
      <DialogTitle>{item?.id ? 'Editar' : 'Adicionar'} Registro</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DialogContentText>
              Digite o histórico do registro:
            </DialogContentText>
          </Grid>
          <Grid item xs={12}>
            <Input rows={10} multiline name="historico" control={control} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          loading={postLoading}
          onClick={handleSubmit(onConfirm)}
        >
          Salvar
        </Button>
        <Button variant="contained" color="secondary" onClick={closeModal}>
          Salvar
        </Button>
      </DialogActions>
    </>
  );
};

const Registros = () => {
  const { id } = useParams();
  const { openModal, closeModal } = useModal();
  const [historicos, setHistoricos] = useState([]);
  const { user } = useContext(AuthContext);
  const { deleteGrid } = useContext(GridContext);
  const { getLoading, getDocumento, postDocumento } =
    useContext(DocumentosContext);

  const loadData = () => {
    const cb = (data) => {
      setHistoricos(
        (data?.DocumentoHists || [])?.map((h) => ({
          id: h?.id,
          titulo: h?.Statusdc?.descricao || null,
          status: h?.Statusdc?.status || null,
          historico: h?.historico || null,
          datahora: h?.datahora || null,
          usuario: h?.User?.name || null,
          usuario_id: h?.User?.id || null,
          imagens: (h?.DocumentoHistmids || [])
            ?.filter((f) => f?.Midium?.tipo === 'IMAGEM')
            ?.map((i) => i?.Midium?.url),
        }))
      );
    };
    closeModal();
    getDocumento({ id, cb });
  };

  const handleSubmit = (data) => postDocumento({ data, cb: loadData });

  const handleDelete = (id) => {
    const onDelete = () => {
      deleteGrid({ params: { rotina: 'DocumentoHist', id }, cb: loadData });
    };
    openModal(<DeleteModal onSubmit={onDelete} />);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONCLUIDO':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'CANCELADO':
        return 'error';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (getLoading) {
    return <Loader />;
  }

  return (
    <Container>
      <Header titulo="Registros da Garantia" />
      <Stepper orientation="vertical">
        {historicos?.map((item, index) => (
          <Step key={index} active expanded>
            <StepLabel>
              <Box display="flex" alignItems="center" gap={1}>
                {Boolean(item?.titulo) && (
                  <Typography fontWeight="bold">{item?.titulo}</Typography>
                )}
                {Boolean(item?.status) && (
                  <Chip
                    size="small"
                    label={item?.status}
                    color={getStatusColor(item?.status)}
                  />
                )}
                {!Boolean(item?.status) &&
                  Boolean(item?.usuario_id === user?.id) && (
                    <Box ml="auto" display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          openModal(
                            <Modal
                              item={item}
                              documento_id={id}
                              onSubmit={handleSubmit}
                            />
                          )
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item?.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
              </Box>
            </StepLabel>
            <StepContent>
              <Paper elevation={0} sx={styles?.paper}>
                {Boolean(item?.historico) && (
                  <Typography variant="body2" color="text.secondary">
                    {item?.historico}
                  </Typography>
                )}
                {item?.imagens?.length > 0 && (
                  <>
                    <Divider sx={styles?.divider} />
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {item?.imagens?.map((img, idx) => (
                        <Box
                          key={idx}
                          src={img}
                          loading="lazy"
                          component="img"
                          sx={styles?.img}
                          onClick={() => window.open(img, '_blank')}
                        />
                      ))}
                    </Box>
                  </>
                )}
                <Divider sx={styles?.divider} />
                {Boolean(item?.datahora) && (
                  <Typography
                    display="block"
                    variant="caption"
                    color="text.secondary"
                  >
                    {moment(item?.datahora).format('DD/MM/YYYY [às] HH:mm')}
                  </Typography>
                )}
                {Boolean(item?.usuario) && (
                  <Typography
                    display="block"
                    variant="caption"
                    color="text.secondary"
                  >
                    Por: {item?.usuario}
                  </Typography>
                )}
              </Paper>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      <Fab
        color="primary"
        sx={styles?.fab}
        onClick={() =>
          openModal(<Modal documento_id={id} onSubmit={handleSubmit} />)
        }
      >
        <Add />
      </Fab>
    </Container>
  );
};

export default Registros;
